import { Location } from '@/lib/types';
import type { MapEntityArea, MapEntityPoint, MapEntityRef, MapAreaStyle, MapEntityPath, MapPathStyle, MapLabel } from '@common/schemas/entities/location.schema';

export type { MapEntityArea, MapEntityPoint, MapEntityRef, MapAreaStyle, MapEntityPath, MapPathStyle, MapLabel };

export interface MapCanvasHandle {
    getComposite: () => Promise<Blob | null>;
    getBuffers: () => Promise<{ land: Blob | null, color: Blob | null, symbols: Blob | null }>;
    getSchematic: () => string;
    resetView: () => void;
    zoomIn: () => void;
    zoomOut: () => void;
}

export interface PlacedSymbol {
    uid: string;
    id: string;
    x: number;
    y: number;
    size: number;
    rotation?: number;
}

export type MapSection = 'land' | 'paths' | 'symbols' | 'labels' | 'overlay' | 'world';
export type LandTool = 'magic' | 'raise' | 'sink' | 'paint' | 'stamp';
export type SymbolMode = 'single' | 'brush' | 'eraser' | 'select';
export type DrawingTool = 'pencil' | 'fill' | 'eraser';
export type WorldSubTab = 'points' | 'areas';

export interface LayerVisibility {
    symbols: boolean;
    rivers: boolean;
    paths: boolean;
    pinpoints: boolean;
    areas: boolean;
    labels: boolean;
    grid: boolean;
    gridType: 'square' | 'hex';
    gridSize: number;
}

export interface MapCanvasProps {
    location: Location | null;
    activeSection: MapSection;
    activeLandTool: LandTool;
    paintOpacity: number;
    brush: string;
    brushSize: number;
    roughness: number;
    tool: DrawingTool;
    activeSymbol: string;
    activeSymbols?: string[];
    layerVisibility: LayerVisibility;
    setActiveSymbol: (id: string) => void;
    activeSymbolCategory: string;
    symbolMode?: SymbolMode;
    symbolSize?: number;
    symbolStyle?: 'colored' | 'empty';
    stampDensity?: number;
    onCommit: () => void;
    placedSymbols: PlacedSymbol[];
    setPlacedSymbols: React.Dispatch<React.SetStateAction<PlacedSymbol[]>>;
    // Asset Hoisting
    isAssetsLoading: boolean;
    lastUpdated: number;
    landRef: React.RefObject<HTMLCanvasElement>;
    colorRef: React.RefObject<HTMLCanvasElement>;
    symbolsRef: React.RefObject<HTMLCanvasElement>;
    assemblerRef: React.RefObject<HTMLCanvasElement>;
    landMaskRef: React.RefObject<Uint8Array | null>;
    stampMaskRef: React.RefObject<Uint8Array | null>;
    patternsRef: React.RefObject<{ land: CanvasPattern | null, water: CanvasPattern | null }>;
    processedSheets: Record<string, HTMLCanvasElement | HTMLImageElement>;
    customMapImageRef?: React.RefObject<HTMLImageElement | null>;
    historyTick?: number;
    // World entity objects
    mapEntityPoints: MapEntityPoint[];
    setMapEntityPoints: React.Dispatch<React.SetStateAction<MapEntityPoint[]>>;
    mapEntityAreas: MapEntityArea[];
    setMapEntityAreas: React.Dispatch<React.SetStateAction<MapEntityArea[]>>;
    isPlacingPoint: boolean;
    isDrawingArea: boolean;
    onPointPlaced: (x: number, y: number) => void;
    onAreaDrawn: (vertices: { x: number; y: number }[]) => void;
    selectedWorldUid: string | null;
    setSelectedWorldUid: (uid: string | null) => void;
    inProgressVertices: { x: number; y: number }[];
    cursorVertex: { x: number; y: number } | null;
    setCursorVertex: React.Dispatch<React.SetStateAction<{ x: number; y: number } | null>>;
    addVertex: (x: number, y: number) => void;
    finishDrawing: () => void;
    // Path objects
    mapEntityPaths: MapEntityPath[];
    setMapEntityPaths: React.Dispatch<React.SetStateAction<MapEntityPath[]>>;
    isDrawingPath: boolean;
    selectedPathUid: string | null;
    setSelectedPathUid: (uid: string | null) => void;
    inProgressPathVertices: { x: number; y: number }[];
    pathCursorVertex: { x: number; y: number } | null;
    setPathCursorVertex: React.Dispatch<React.SetStateAction<{ x: number; y: number } | null>>;
    addPathVertex: (x: number, y: number) => void;
    finishPathDrawing: (finalSnap?: { x: number; y: number }) => void;
    movePathVertex: (uid: string, index: number, x: number, y: number) => void;
    insertPathVertex: (uid: string, index: number, x: number, y: number) => void;
    deletePathVertex: (uid: string, index: number) => void;
    activePathTypeRef?: React.RefObject<{ pathType: import('@common/schemas/entities/location.schema').MapPathType; roadQuality?: import('@common/schemas/entities/location.schema').MapRoadQuality }>;
    // Labels
    mapLabels: MapLabel[];
    setMapLabels: React.Dispatch<React.SetStateAction<MapLabel[]>>;
    selectedLabelUid: string | null;
    setSelectedLabelUid: (uid: string | null) => void;
    hoveredLabelUid?: string | null;
}

export const BUFFER_SIZE = 1024;
export const ASPECT_RATIO = 1;
